import { ask } from '@monorepo/tools/src/lib/tools/ask/ask';
import endpoints from './endpoints/index.endpoints';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import {
	BidderCampaignTableModel,
	BidderCampaignGroup,
	IBidderAdvertiser,
	IBidderCampaign,
	IBidderDeepCloneCampaign,
} from '../models/bidder-campaign/bidder-camapign.model';
import { IBidderAdvertiser as IBidderAdvertiserPage } from '../models/bidder-advertiser.model';
import { UpdateCampaignResponse } from '../models/bidder-campaign/bidder-campaign.types';
import { IApiChangeLogsResponse } from '../models/bidder-logs.model';
import { IBidderModelUpdatePayload, IGetBidderModelsResponse } from '../models/bidder-model.model';

export type ISetBadIspWordsPerAdvertiserBody = {
	advertiser_bad_isp_words: { advertiser_id: id; bad_isp_words: string };
};

export type ISetBadIspWordsPerCampaignBody = {
	campaign_bad_isp_words: { campaign_id: id; bad_isp_words: string };
};

export type ISetBadIspWordsAdvertiserResponse = { advertiser_id: id; status: string };
export type ISetBadIspWordsCampaignResponse = { campaign_id: id; status: string };

export interface ITrafficEntityFields {
	geo: string;
	op_system: string;
	browser: string;
	device_type: string;
}

export interface ITrafficEntity extends ITrafficEntityFields {
	id: number;
	rel_camp?: string[];
}

export interface ITrafficEntityError {
	field: string;
	message: string;
}

export interface IGetTrafficEntitiesResponse {
	traffic_entities: ITrafficEntity[];
	success: boolean;
}

export interface ITrafficEntityMutationResponse {
	id: number;
	success: boolean;
}

export const BidderApi = {
	getPublishers: (): Promise<{ success: boolean; publishers: { id: id; name: string }[] }> => {
		return ask.get(endpoints.bidder.getPublishers());
	},

	getCampaignGroups: (id: id): Promise<{ success: boolean; campaign_groups: BidderCampaignGroup[] }> => {
		return ask.get(endpoints.bidder.getCampaignGroups(id));
	},

	getStateMachineIds: (): Promise<{ state_machine_ids: id[]; status: string }> => {
		return ask.get(endpoints.bidder.getStateMachineIds());
	},

	getBidderCampaign: (id: id): Promise<IBidderCampaign> => {
		return ask.get(endpoints.bidder.getCampaign(id));
	},

	getBidderAdvertiser: (id: id): Promise<IBidderAdvertiserPage> => {
		return ask.get(endpoints.bidder.getAdvertiser(id));
	},

	getAdvertisers: (): Promise<{ success: boolean; advertisers: IBidderAdvertiser[] }> => {
		return ask.get(endpoints.bidder.getAdvertisers());
	},

	getCampaigns: (): Promise<{ campaigns: BidderCampaignTableModel[] }> => {
		return ask.get(endpoints.bidder.getCampaigns());
	},

	getModels: (): Promise<IGetBidderModelsResponse> => {
		return ask.get(endpoints.bidder.getModels());
	},

	updateModel: (body: { update_model: IBidderModelUpdatePayload }): Promise<{ success?: boolean }> => {
		return ask.post(endpoints.bidder.updateModel(), body);
	},

	rebuildModel: (modelName: string): Promise<{ success?: boolean }> => {
		return ask.get(endpoints.bidder.rebuildModel(modelName));
	},

	createCampaign: (body: IBidderCampaign): Promise<UpdateCampaignResponse> => {
		return ask.post(endpoints.bidder.createCampaign(), { campaign: body });
	},

	createAdvertiser: (body: IBidderAdvertiserPage): Promise<{ success: boolean; advertiser: IBidderAdvertiserPage }> => {
		return ask.post(endpoints.bidder.createAdvertiser(), { advertiser: body });
	},

	cloneCampaign: (body: { campaign: IBidderCampaign; deep_clone: IBidderDeepCloneCampaign }): Promise<UpdateCampaignResponse> => {
		return ask.post(endpoints.bidder.cloneCampaign(), body);
	},

	updateCampaign: (body: IBidderCampaign): Promise<UpdateCampaignResponse> => {
		return ask.put(endpoints.bidder.updateCampaign(), { campaign: body });
	},

	updateAdvertiser: (body: IBidderAdvertiserPage): Promise<{ success: boolean; advertiser: IBidderAdvertiserPage }> => {
		return ask.post(endpoints.bidder.updateAdvertiser(), { advertiser: body });
	},

	enableCampaign: (campaignId: id): Promise<UpdateCampaignResponse> => {
		return ask.put(endpoints.bidder.enableCampaign(campaignId), {});
	},

	enableAdvertiser: (advertiserId: id): Promise<{ success: boolean; advertiser: IBidderAdvertiserPage }> => {
		return ask.put(endpoints.bidder.enableAdvertiser(advertiserId), {});
	},

	pauseCampaign: (campaignId: id): Promise<UpdateCampaignResponse> => {
		return ask.put(endpoints.bidder.pauseCampaign(campaignId), {});
	},

	pauseAdvertiser: (advertiserId: id): Promise<{ success: boolean; advertiser: IBidderAdvertiserPage }> => {
		return ask.put(endpoints.bidder.pauseAdvertiser(advertiserId), {});
	},

	ackCampaignNote: (campaignId: id) => {
		return ask.post(endpoints.bidder.ackCampaignNote(campaignId), {});
	},

	getApiChangesLogData: (ts_start?: string, ts_end?: string, id?: id): Promise<IApiChangeLogsResponse> => {
		return ask.get(endpoints.bidder.getApiChangesLogData({ ts_start, ts_end, id }));
	},

	getApiChangesLogDataWithParams: (params?: URLSearchParams): Promise<IApiChangeLogsResponse> => {
		const ts_start = params?.get('ts_start') || undefined;
		const ts_end = params?.get('ts_end') || undefined;
		const id = params?.get('id') ? Number(params.get('id')) : undefined;
		return BidderApi.getApiChangesLogData(ts_start, ts_end, id);
	},

	downloadApiChangesLogData: (ts_start?: string, ts_end?: string, id?: id): Promise<any> => {
		return ask.download(endpoints.bidder.downloadApiChangesLogData({ ts_start, ts_end, id }));
	},
	deleteTargetingLinkFromCache: (body: { campaign_id: id; yids: string[] }): Promise<{ success: boolean }> => {
		return ask.post(endpoints.bidder.deleteTargetingLinkCache(), body);
	},

	setBadIspWordsPerAdvertiser: (body: ISetBadIspWordsPerAdvertiserBody): Promise<ISetBadIspWordsAdvertiserResponse> => {
		return ask.post(endpoints.bidder.setBadIspWordsPerAdvertiser(), body);
	},

	setBadIspWordsPerCampaign: (body: ISetBadIspWordsPerCampaignBody): Promise<ISetBadIspWordsCampaignResponse> => {
		return ask.post(endpoints.bidder.setBadIspWordsPerCampaign(), body);
	},

	getTrafficEntities: (): Promise<IGetTrafficEntitiesResponse> => {
		return ask.get(endpoints.bidder.getTrafficEntities());
	},

	createTrafficEntity: (traffic: ITrafficEntityFields): Promise<ITrafficEntityMutationResponse> => {
		return ask.post(endpoints.bidder.createTrafficEntity(), { traffic });
	},

	updateTrafficEntity: (traffic: ITrafficEntityFields & { id: number }): Promise<ITrafficEntityMutationResponse> => {
		return ask.post(endpoints.bidder.updateTrafficEntity(), { traffic });
	},
};
